import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  showPrevNext?: boolean;
  showFirstLast?: boolean;
  maxVisiblePages?: number;
  disabled?: boolean;
  className?: string;
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  showPrevNext = true,
  showFirstLast = false,
  maxVisiblePages = 7,
  disabled = false,
  className
}: PaginationProps) {
  if (totalPages <= 1) {
    return null;
  }

  const pages = getVisiblePages(currentPage, totalPages, maxVisiblePages);
  
  return (
    <div className={cn("flex items-center justify-center space-x-1", className)}>
      {/* First page */}
      {showFirstLast && currentPage > 1 && (
        <>
          <PaginationButton
            onClick={() => onPageChange(1)}
            disabled={disabled}
            variant="outline"
          >
            1
          </PaginationButton>
          {currentPage > 3 && (
            <PaginationEllipsis />
          )}
        </>
      )}

      {/* Previous */}
      {showPrevNext && (
        <PaginationButton
          onClick={() => onPageChange(currentPage - 1)}
          disabled={disabled || currentPage <= 1}
          variant="outline"
          className="px-2"
        >
          <ChevronLeft className="h-4 w-4" />
        </PaginationButton>
      )}

      {/* Page numbers */}
      {pages.map((page, index) => {
        if (page === '...') {
          return <PaginationEllipsis key={`ellipsis-${index}`} />;
        }
        
        const pageNum = page as number;
        return (
          <PaginationButton
            key={pageNum}
            onClick={() => onPageChange(pageNum)}
            disabled={disabled}
            variant={pageNum === currentPage ? "default" : "outline"}
            isActive={pageNum === currentPage}
          >
            {pageNum}
          </PaginationButton>
        );
      })}

      {/* Next */}
      {showPrevNext && (
        <PaginationButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={disabled || currentPage >= totalPages}
          variant="outline"
          className="px-2"
        >
          <ChevronRight className="h-4 w-4" />
        </PaginationButton>
      )}

      {/* Last page */}
      {showFirstLast && currentPage < totalPages && (
        <>
          {currentPage < totalPages - 2 && (
            <PaginationEllipsis />
          )}
          <PaginationButton
            onClick={() => onPageChange(totalPages)}
            disabled={disabled}
            variant="outline"
          >
            {totalPages}
          </PaginationButton>
        </>
      )}
    </div>
  );
}

interface PaginationButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: "default" | "outline";
  isActive?: boolean;
  className?: string;
}

function PaginationButton({
  children,
  onClick,
  disabled = false,
  variant = "outline",
  isActive = false,
  className
}: PaginationButtonProps) {
  return (
    <Button
      variant={variant}
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "h-9 w-9 p-0",
        isActive && "bg-primary text-primary-foreground",
        className
      )}
    >
      {children}
    </Button>
  );
}

function PaginationEllipsis() {
  return (
    <div className="flex h-9 w-9 items-center justify-center">
      <MoreHorizontal className="h-4 w-4" />
    </div>
  );
}

function getVisiblePages(
  currentPage: number, 
  totalPages: number, 
  maxVisible: number
): (number | string)[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const halfVisible = Math.floor(maxVisible / 2);
  
  if (currentPage <= halfVisible + 1) {
    // Show from start
    const pages = Array.from({ length: maxVisible - 1 }, (_, i) => i + 1);
    return [...pages, '...', totalPages];
  }
  
  if (currentPage >= totalPages - halfVisible) {
    // Show from end
    const pages = Array.from({ length: maxVisible - 1 }, (_, i) => totalPages - maxVisible + 2 + i);
    return [1, '...', ...pages];
  }
  
  // Show around current
  const start = currentPage - halfVisible + 1;
  const end = currentPage + halfVisible - 1;
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
  
  return [1, '...', ...pages, '...', totalPages];
}

export { PaginationButton, PaginationEllipsis };
